/**
 * <TutorRevisionProgramSheet> — le programme de révision d'un élève, vu par son tuteur (template).
 *
 * L'ancienne `StudyProgramChapterSelectionModal.js`, dans ses deux usages tuteur :
 *  - **lecture** (`mode="read"`, « Programme de révision » depuis la fiche de l'élève) : rien ne
 *    presse ;
 *  - **choix** (`mode="pick"`, « Sélectionner un chapitre » depuis le « Mode
 *    programme de révision » de « Générer une activité ») : toucher un chapitre le retient.
 * Dans les deux, l'ordre du legacy : le choix du programme quand l'élève en a plusieurs
 * (`StudyProgramsFilterList` — ses matières et « Mathématiques - 4ème - … »), puis le programme
 * jour par jour, chaque chapitre avec la progression de l'élève (`showChapterProgress`).
 *
 * La page « Programme de révision » du parent (`<RevisionProgramPage>`) montre des journées sans
 * progression ni choix : elle ne couvre pas ce cas, d'où cette feuille.
 *
 * Contrôlée : les programmes, celui qui est montré, le chargement arrivent en props. Une lecture
 * refusée (`error`) se dit avec « Réessayer », comme une page (`<PageTemplate>`) : jamais « Aucun
 * programme » pour un programme qui n'a pas pu se lire.
 */
import React from "react"

import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Stack } from "../../atoms/containers/Stack"
import { DelayedSkeleton, Skeleton } from "../../atoms/Skeleton"
import { Title } from "../../atoms/typography/Title"
import { HorizontalList } from "../../atoms/HorizontalList"
import { CardList, CardListRow } from "../../molecules/CardList"
import { EmptyState } from "../../molecules/EmptyState"
import { Modal } from "../../molecules/Modal"
import { TutorProgramChapterRow } from "../../molecules/TutorProgramChapterRow"
import { type UITutorProgramChapter, type UITutorStudyProgram } from "../../../ports/primary/ui/types/tutorEleves"

const TEXTS = {
    read: "Programme de révision",
    pick: "Sélectionner un chapitre",
    empty: "Aucun programme de révision trouvé",
}

export type TutorRevisionProgramSheetProps = {
    visible: boolean
    onClose: () => void
    /** `read` : le programme se lit ; `pick` : un chapitre se choisit. */
    mode?: "read" | "pick"
    programs: UITutorStudyProgram[]
    /** Le programme montré. Défaut : le premier. */
    selectedProgramId?: UITutorStudyProgram["id"] | null
    onSelectProgram?: (id: UITutorStudyProgram["id"]) => void
    /** Le chapitre choisi (`mode="pick"`). */
    onPickChapter?: (chapter: UITutorProgramChapter) => void
    loading?: boolean
    /** La lecture des programmes a échoué : son message, et « Réessayer ». `null` : rien. */
    error?: string | null
    onRetry?: () => void
    testID?: string
}

export function TutorRevisionProgramSheet(props: TutorRevisionProgramSheetProps) {
    const {
        visible,
        onClose,
        mode = "read",
        programs,
        selectedProgramId,
        onSelectProgram,
        onPickChapter,
        loading = false,
        error = null,
        onRetry,
        testID,
    } = props
    const program = programs.find((p) => p.id === selectedProgramId) ?? programs[0] ?? null
    const picking = mode === "pick" && onPickChapter != null

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title={mode === "pick" ? TEXTS.pick : TEXTS.read}
            fullScreenOnSmall
            testID={testID}
        >
            {loading ? (
                <DelayedSkeleton accessibilityLabel="Chargement du programme">
                    <Stack gap="4">
                        <Skeleton textSize="text-lg" width={200} />
                        <CardList elevation="none">
                            {[0, 1, 2].map((index) => (
                                <CardListRow key={index} divided={index > 0}>
                                    <Stack gap="1">
                                        <Skeleton textSize="text-sm" width={110} />
                                        <Skeleton textSize="text-md" width={220} />
                                        <Skeleton shape="block" height={8} radius="full" />
                                    </Stack>
                                </CardListRow>
                            ))}
                        </CardList>
                    </Stack>
                </DelayedSkeleton>
            ) : error != null ? (
                <Stack gap="4" align="center">
                    <Banner message={error} color="danger" icon="exclamation-circle" tinted />
                    {onRetry != null && <Button label="Réessayer" onPress={onRetry} />}
                </Stack>
            ) : program == null ? (
                <EmptyState iconName="book-open" title={TEXTS.empty} />
            ) : (
                <Stack gap="6">
                    {programs.length > 1 && (
                        <HorizontalList gap="2">
                            {programs.map((p) => (
                                <ChoiceChip
                                    key={p.id}
                                    label={p.label}
                                    selected={p.id === program.id}
                                    onPress={() => onSelectProgram?.(p.id)}
                                />
                            ))}
                        </HorizontalList>
                    )}
                    {program.days.map((day) => (
                        <Stack key={day.id} gap="3">
                            <Title size="text-lg">{day.title}</Title>
                            <CardList>
                                {day.chapters.map((chapter, index) => (
                                    <TutorProgramChapterRow
                                        key={chapter.id}
                                        chapter={chapter}
                                        divided={index > 0}
                                        onPress={picking ? () => onPickChapter!(chapter) : undefined}
                                    />
                                ))}
                            </CardList>
                        </Stack>
                    ))}
                </Stack>
            )}
        </Modal>
    )
}
